/**
 * Step 3: Bank Details (Stripe Connect)
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ChevronLeft, ExternalLink, CheckCircle, CreditCard, Building } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BankDetailsStep({ data, onComplete, onBack }) {
  const [method, setMethod] = useState(data?.use_manual_bank ? 'manual' : 'stripe');
  const [stripeStatus, setStripeStatus] = useState({
    connected: !!data?.stripe_account_id,
    detailsSubmitted: data?.stripe_onboarding_complete || false
  });
  const [manualData, setManualData] = useState({
    legal_business_name: data?.legal_business_name || '',
    bank_name: data?.bank_name || '',
    routing_number: '',
    account_number: '',
    account_type: data?.account_type || 'checking',
    tax_id: data?.tax_id || ''
  });
  const [saving, setSaving] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);

  // Check Stripe status on mount
  useEffect(() => {
    checkStripeStatus();
  }, []);

  const checkStripeStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/retailer/payouts/stripe/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setStripeStatus({
          connected: !!result.stripe_account_id,
          detailsSubmitted: result.details_submitted || false,
          payoutsEnabled: result.payouts_enabled || false
        });
      }
    } catch (error) {
      console.error('Failed to check Stripe status:', error);
    }
  };

  const handleStripeConnect = async () => {
    setConnectLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/retailer/payouts/stripe/connect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();

      if (response.ok && result.onboarding_url) {
        // Open Stripe Connect in new tab
        window.open(result.onboarding_url, '_blank');
        toast.info('Complete the Stripe setup in the new tab, then return here');
        
        // Poll for completion
        const pollInterval = setInterval(async () => {
          await checkStripeStatus();
        }, 3000);
        
        // Stop polling after 5 minutes
        setTimeout(() => clearInterval(pollInterval), 300000);
      } else {
        toast.error(result.detail || 'Failed to start Stripe Connect');
      }
    } catch (error) {
      toast.error('Failed to connect Stripe');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleManualChange = (field, value) => {
    setManualData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      let endpoint, body;

      if (method === 'stripe') {
        // Mark Stripe step as complete
        endpoint = `${API_URL}/api/retailer/onboarding/bank-details/complete`;
        body = {};
      } else {
        // Save manual bank details
        endpoint = `${API_URL}/api/retailer/onboarding/bank-details/manual`;
        body = manualData;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Bank details saved!');
        onComplete(result);
      } else {
        toast.error(result.detail || 'Failed to save bank details');
      }
    } catch (error) {
      toast.error('Failed to save bank details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Payment Method Selection */}
      <div>
        <Label className="text-base font-medium mb-4 block">How would you like to receive payments?</Label>
        <RadioGroup value={method} onValueChange={setMethod} className="space-y-4">
          {/* Stripe Connect Option */}
          <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${
            method === 'stripe' ? 'border-emerald-500 bg-emerald-50' : 'hover:border-gray-300'
          }`}>
            <div className="flex items-start gap-3">
              <RadioGroupItem value="stripe" id="stripe" className="mt-1" />
              <div className="flex-1">
                <label htmlFor="stripe" className="font-medium cursor-pointer flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                  Stripe Connect (Recommended)
                </label>
                <p className="text-sm text-gray-600 mt-1">
                  Fast, secure payouts directly to your bank account. Includes identity verification.
                </p>
                
                {method === 'stripe' && (
                  <div className="mt-4 p-4 bg-white rounded-lg border">
                    {stripeStatus.detailsSubmitted ? (
                      <div className="flex items-center gap-2 text-emerald-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Stripe Connected!</span>
                      </div>
                    ) : stripeStatus.connected ? (
                      <div>
                        <p className="text-sm text-amber-600 mb-2">
                          Stripe account created but setup incomplete
                        </p>
                        <Button
                          type="button"
                          onClick={handleStripeConnect}
                          disabled={connectLoading}
                          className="bg-[#635bff] hover:bg-[#5851db]"
                        >
                          {connectLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <ExternalLink className="w-4 h-4 mr-2" />
                          )}
                          Complete Stripe Setup
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        onClick={handleStripeConnect}
                        disabled={connectLoading}
                        className="bg-[#635bff] hover:bg-[#5851db]"
                        data-testid="stripe-connect-btn"
                      >
                        {connectLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <ExternalLink className="w-4 h-4 mr-2" />
                        )}
                        Connect with Stripe
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Manual Bank Option */}
          <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${
            method === 'manual' ? 'border-emerald-500 bg-emerald-50' : 'hover:border-gray-300'
          }`}>
            <div className="flex items-start gap-3">
              <RadioGroupItem value="manual" id="manual" className="mt-1" />
              <div className="flex-1">
                <label htmlFor="manual" className="font-medium cursor-pointer flex items-center gap-2">
                  <Building className="w-5 h-5 text-gray-600" />
                  Manual Bank Details
                </label>
                <p className="text-sm text-gray-600 mt-1">
                  Enter your bank information manually. Payouts may take longer to process.
                </p>
                
                {method === 'manual' && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <Label htmlFor="legal_business_name">Legal Business Name *</Label>
                      <Input
                        id="legal_business_name"
                        value={manualData.legal_business_name}
                        onChange={(e) => handleManualChange('legal_business_name', e.target.value)}
                        placeholder="As registered with IRS"
                        required={method === 'manual'}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="tax_id">Tax ID (EIN) *</Label>
                      <Input
                        id="tax_id"
                        value={manualData.tax_id}
                        onChange={(e) => handleManualChange('tax_id', e.target.value)}
                        placeholder="XX-XXXXXXX"
                        required={method === 'manual'}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="bank_name">Bank Name *</Label>
                      <Input
                        id="bank_name"
                        value={manualData.bank_name}
                        onChange={(e) => handleManualChange('bank_name', e.target.value)}
                        placeholder="Your bank name"
                        required={method === 'manual'}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="routing_number">Routing Number *</Label>
                        <Input
                          id="routing_number"
                          value={manualData.routing_number}
                          onChange={(e) => handleManualChange('routing_number', e.target.value)}
                          placeholder="XXXXXXXXX"
                          maxLength={9}
                          required={method === 'manual'}
                        />
                      </div>
                      <div>
                        <Label htmlFor="account_number">Account Number *</Label>
                        <Input
                          id="account_number"
                          type="password"
                          value={manualData.account_number}
                          onChange={(e) => handleManualChange('account_number', e.target.value)}
                          placeholder="XXXXXXXXXXXX"
                          required={method === 'manual'}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Account Type *</Label>
                      <Select
                        value={manualData.account_type}
                        onValueChange={(value) => handleManualChange('account_type', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select account type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="checking">Checking</SelectItem>
                          <SelectItem value="savings">Savings</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </RadioGroup>
      </div>

      {/* Actions */}
      <div className="flex justify-between gap-3 pt-4">
        {onBack && (
          <Button type="button" variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
        )}
        <Button
          type="submit"
          disabled={saving || (method === 'stripe' && !stripeStatus.detailsSubmitted)}
          className="bg-emerald-600 hover:bg-emerald-700 ml-auto"
          data-testid="save-bank-details-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save & Continue'
          )}
        </Button>
      </div>
    </form>
  );
}
